/**
 * Copyright by:
 *
 * by ESS-Ehrhardt Software Solutions & Service GmbH
 * Basselscheider Str. 55
 * D-56281 Emmelshausen
 * +49 (0) 67 42 / 87 27 0
 * info@epg.com
 */
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { Router } from '@angular/router';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faXmark,
  faMagnifyingGlass,
  faCheck,
  faChevronDown
} from '@fortawesome/pro-solid-svg-icons';
import { Subscription } from 'rxjs/internal/Subscription';
import { ChipsElementModel } from '../chips-autocomplete/chips-autocomplete.component';

export interface GroupedChipsGroup {
  key: string;
  label: string;
  icon?: IconDefinition;
  items: ChipsElementModel[];
}

interface SelectedItem {
  element: ChipsElementModel;
  groupKey: string;
  groupIcon?: IconDefinition;
}

@Component({
  selector: 'app-grouped-chips-autocomplete',
  templateUrl: './grouped-chips-autocomplete.component.html',
  styleUrls: ['./grouped-chips-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => GroupedChipsAutocompleteComponent)
    }
  ],
  standalone: false
})
export class GroupedChipsAutocompleteComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  faXmark = faXmark;
  faMagnifyingGlass = faMagnifyingGlass;
  faCheck = faCheck;
  faChevronDown = faChevronDown;

  controlValueChangesSubscription!: Subscription;
  control: FormControl = new FormControl('');
  private justSelected = false;

  @Input() title!: string;
  @Input() inputPlaceholder!: string;
  @Input() groups: GroupedChipsGroup[] = [];
  @Input() multiSelect: boolean = true;
  @Input() multiGroupSelect: boolean = true;
  @Input() singleSelect: boolean = false;
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';
  @Input() value: Record<string, string[]> = {};
  @Input() optionTemplate: TemplateRef<any> | null = null;

  filteredGroups: GroupedChipsGroup[] = [];
  selectedItems: SelectedItem[] = [];

  @ViewChild('elementsInput') elementsInput!: ElementRef<HTMLInputElement>;
  @ViewChild('auto') autocomplete!: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;
  @ViewChild('autocompleteTrigger', { read: MatAutocompleteTrigger })
  autocompleteTriggerRef!: MatAutocompleteTrigger;
  @ViewChild('chipGrid') chipGrid: any;

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  private controlValue: Record<string, string[]> = {};
  private controlValueOnChange: (value: any) => void = () => {};
  private controlValueOnTouched: () => void = () => {};

  @Output() selectionchange = new EventEmitter<Record<string, string[]>>();
  @Output() invalidIdsDetected = new EventEmitter<{ groupKey: string; invalidIds: string[] }[]>();

  constructor(private router: Router) {}

  ngAfterViewInit(): void {
    if (this.controlValue) {
      this.writeValue(this.controlValue);
    }
    if (this.controlValueOnChange) {
      this.registerOnChange(this.controlValueOnChange);
    }
    if (this.controlValueOnTouched) {
      this.registerOnTouched(this.controlValueOnTouched);
    }

    if (this.autoTrigger) {
      this.autoTrigger.panelClosingActions.subscribe((event) => {
        if (!this.singleSelect) {
          return;
        }

        // event === null => panel closed without a selection (e.g. click outside, Tab)
        if (!event) {
          this.updateSelectedItems();
        }
      });
    }
  }

  writeValue(obj: any): void {
    this.controlValue = obj || {};
    this.updateSelectedItems();
  }

  registerOnChange(fn: any): void {
    this.controlValueOnChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.controlValueOnTouched = fn;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.value && !changes.value.firstChange) {
      this.writeValue(changes.value.currentValue);
    }
    if (changes.groups) {
      this.groups = changes.groups.currentValue || [];
      // Update filtered groups when groups input changes - use this.groups which is now updated
      this.filteredGroups = this.filterGroupsInternal(this.groups, this.control.value);

      if (!changes.groups.firstChange) {
        this.cleanupControlValue();
        this.updateSelectedItems();
      }
    }
  }

  ngOnDestroy(): void {
    this.controlValueChangesSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.filteredGroups = this.groups;
    this.controlValueChangesSubscription = this.control.valueChanges.subscribe((value) => {
      this.filteredGroups = this.filterGroups(value);
    });
  }

  private filterGroups(searchValue: string | null): GroupedChipsGroup[] {
    return this.filterGroupsInternal(this.groups, searchValue);
  }

  private filterGroupsInternal(
    groups: GroupedChipsGroup[],
    searchValue: string | null
  ): GroupedChipsGroup[] {
    if (!searchValue || typeof searchValue !== 'string') {
      const result = groups.filter((group) => group.items.length > 0);
      return result;
    }

    const filterValue = searchValue.toLowerCase();
    const result = groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.name.toLowerCase().includes(filterValue))
      }))
      .filter((group) => group.items.length > 0);
    return result;
  }

  isSelected(item: ChipsElementModel, groupKey: string): boolean {
    const selectedIds = this.controlValue[groupKey] || [];
    return selectedIds.includes(item.id);
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value;
    if (value && value.item && value.group) {
      this.select(value.item, value.group);
    }
  }

  select(item: ChipsElementModel, group: GroupedChipsGroup): void {
    if (this.singleSelect) {
      // Single-select mode: replace value and show in input
      this.controlValue = { [group.key]: [item.id] };
      this.updateSelectedItems();
      this.justSelected = true;
      if (this.elementsInput && this.elementsInput.nativeElement) {
        this.elementsInput.nativeElement.value = item.name;
        setTimeout(() => {
          this.elementsInput.nativeElement.setSelectionRange(0, 0);
          this.elementsInput.nativeElement.blur();
        });
      }
      this.control.setValue(item.name, { emitEvent: false });
    } else if (!this.multiSelect) {
      this.controlValue = { [group.key]: [item.id] };
      this.updateSelectedItems();
      if (this.elementsInput && this.elementsInput.nativeElement) {
        this.elementsInput.nativeElement.value = '';
      }
      this.control.setValue('', { emitEvent: false });
    } else {
      if (!this.multiGroupSelect) {
        const hasOtherGroupSelections = Object.keys(this.controlValue).some(
          (key) => key !== group.key && this.controlValue[key]?.length > 0
        );
        if (hasOtherGroupSelections) {
          this.controlValue = {};
        }
      }

      if (!this.controlValue[group.key]) {
        this.controlValue[group.key] = [];
      }

      if (!this.controlValue[group.key].includes(item.id)) {
        this.controlValue[group.key].push(item.id);
      }
      this.updateSelectedItems();
      if (this.elementsInput && this.elementsInput.nativeElement) {
        this.elementsInput.nativeElement.value = '';
      }
      this.control.setValue('', { emitEvent: false });
    }

    this.emitChanges();
  }

  remove(selectedItem: SelectedItem): void {
    const groupKey = selectedItem.groupKey;
    if (this.controlValue[groupKey]) {
      const index = this.controlValue[groupKey].indexOf(selectedItem.element.id);
      if (index >= 0) {
        this.controlValue[groupKey].splice(index, 1);
        if (this.controlValue[groupKey].length === 0) {
          delete this.controlValue[groupKey];
        }
      }
    }

    this.updateSelectedItems();
    this.emitChanges();
  }

  clearSelection(): void {
    this.controlValue = {};
    this.selectedItems = [];
    if (this.elementsInput && this.elementsInput.nativeElement) {
      this.elementsInput.nativeElement.value = '';
    }
    this.control.setValue('', { emitEvent: false });
    this.emitChanges();
  }

  updateSelectedItems(): void {
    this.selectedItems = [];

    for (const group of this.groups) {
      const selectedIds = this.controlValue[group.key] || [];
      for (const id of selectedIds) {
        const element = group.items.find((item) => item.id === id);
        if (element) {
          this.selectedItems.push({
            element,
            groupKey: group.key,
            groupIcon: group.icon
          });
        }
      }
    }

    // In single-select mode, show selected value in input
    if (this.singleSelect) {
      if (this.selectedItems.length > 0) {
        const selectedName = this.selectedItems[0].element.name;
        if (this.elementsInput && this.elementsInput.nativeElement) {
          this.elementsInput.nativeElement.value = selectedName;
          setTimeout(() => {
            this.elementsInput.nativeElement.setSelectionRange(0, 0);
            this.elementsInput.nativeElement.blur();
          });
        }
        this.control.setValue(selectedName, { emitEvent: false });
      } else {
        // No selection - clear the input
        if (this.elementsInput && this.elementsInput.nativeElement) {
          this.elementsInput.nativeElement.value = '';
        }
        this.control.setValue('', { emitEvent: false });
      }
    } else {
      // Multi-select mode - always clear input
      if (this.elementsInput && this.elementsInput.nativeElement) {
        this.elementsInput.nativeElement.value = '';
      }
      this.control.setValue(null, { emitEvent: false });
    }
  }

  private emitChanges(): void {
    this.selectionchange.emit({ ...this.controlValue });
    this.controlValueOnChange(this.controlValue);
    this.controlValueOnTouched();
  }

  private cleanupControlValue(): void {
    const validKeys = new Set(this.groups.map((g) => g.key));
    const newValue: Record<string, string[]> = {};
    const invalidIdsPerGroup: { groupKey: string; invalidIds: string[] }[] = [];

    for (const key of Object.keys(this.controlValue)) {
      if (validKeys.has(key)) {
        const group = this.groups.find((g) => g.key === key);
        if (group) {
          const validIds = this.controlValue[key].filter((id) =>
            group.items.some((item) => item.id === id)
          );
          const invalidIds = this.controlValue[key].filter(
            (id) => !group.items.some((item) => item.id === id)
          );

          if (invalidIds.length > 0) {
            invalidIdsPerGroup.push({ groupKey: key, invalidIds });
          }

          if (validIds.length > 0) {
            newValue[key] = validIds;
          }
        }
      }
    }

    this.controlValue = newValue;

    if (invalidIdsPerGroup.length > 0) {
      this.invalidIdsDetected.emit(invalidIdsPerGroup);
    }
  }

  onChipClick(selectedItem: SelectedItem): void {
    if (selectedItem.element.url) {
      this.router.navigate([`${selectedItem.element.url}${selectedItem.element.id}`]);
    }
  }

  onSingleInputFocused(): void {
    // Don't clear if we just selected an item
    if (this.justSelected) {
      this.justSelected = false;
      return;
    }

    // Clear the filter to show all options when focusing
    if (this.elementsInput && this.elementsInput.nativeElement) {
      this.elementsInput.nativeElement.value = '';
    }
    this.control.setValue('', { emitEvent: false });
    this.filteredGroups = this.filterGroups('');
  }

  onSingleInputBlurred(): void {
    // If the autocomplete panel is/was open, let panelClosingActions decide
    // whether to restore (no selection) or not (selection made).
    if (this.singleSelect && this.autoTrigger?.panelOpen) {
      return;
    }

    // Panel not open (focus->blur without using autocomplete): restore text
    // for the current selection.
    this.updateSelectedItems();
  }

  onInputClick(): void {
    // Clear the input value to prevent filtering
    if (this.elementsInput && this.elementsInput.nativeElement) {
      this.elementsInput.nativeElement.value = '';
    }

    // Clear control value without emitting to prevent valueChanges from filtering
    this.control.setValue('', { emitEvent: false });

    // Manually update filtered groups to show all items
    this.filteredGroups = this.filterGroups('');
  }

  get showGroupHeaders(): boolean {
    return this.groups.length > 1;
  }

  isPanelOpen(): boolean {
    return this.autoTrigger?.panelOpen || false;
  }

  onPanelOpened(): void {
    setTimeout(() => {
      const formFieldEl = this.elementsInput?.nativeElement?.closest('mat-form-field');
      if (formFieldEl && this.autocomplete?.panel) {
        const formFieldWidth = formFieldEl.clientWidth;
        const panelEl = this.autocomplete.panel.nativeElement;
        panelEl.style.minWidth = `${formFieldWidth}px`;
        panelEl.style.maxWidth = `${formFieldWidth * 2}px`;
      }
    });
  }

  isOverflowing(el: HTMLElement): boolean {
    return el.scrollWidth > el.clientWidth;
  }
}
